import {
  ACCOUNT_FEATURE,
  selectAccountId,
  selectAccountName,
  selectUserAccountRole,
  selectUserRolePermissions,
  USER_ROLE,
} from '@socialive/common';
import { useFeature } from '@socialive/features';
import PropTypes from 'prop-types';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory, useLocation } from 'react-router';
import { createChannel, selectAccountChannelsSorted, selectChannelsLoaded, updateChannel } from '~/store';
import AccountNav from '../components/AccountNav';
import ChannelModal, { ChannelModalTypes } from '../components/ChannelModal';
import CustomTable, { ChannelTableRowRenderer } from '../components/CustomTable';
import dynamicallySort from '../helpers/dynamicallySort';

import '../style/channels.css';

const CHANNEL_SORT_BY = {
  NAME: 'name',
  TOKEN: 'token',
  CREATOR_NAME: 'creatorName',
};

const CHANNEL_TABLE_HEADER_ITEMS = [
  { sort: CHANNEL_SORT_BY.NAME, displayName: 'CHANNEL' },
  { sort: CHANNEL_SORT_BY.TOKEN, displayName: 'CHANNEL ID' },
  { sort: CHANNEL_SORT_BY.CREATOR_NAME, displayName: 'CREATOR' },
];

const CHANNEL_SORT_DIRECTION = {
  ASC: 'asc',
  DESC: 'desc',
};

const Channels = ({ reAuth, checkAuthorization }) => {
  const location = useLocation();
  const history = useHistory();
  const dispatch = useDispatch();
  const userRole = useSelector(selectUserAccountRole);
  const accountName = useSelector(selectAccountName);
  const accountId = useSelector(selectAccountId);
  const accountChannels = useSelector(selectAccountChannelsSorted);
  const didChannelsLoad = useSelector(selectChannelsLoaded);
  const { canManageChannels } = useSelector(selectUserRolePermissions);
  const [, { enabled: isUserRolesEnabled }] = useFeature(ACCOUNT_FEATURE.USER_ROLES);
  const [, { enabled: isSpacesEnabled }] = useFeature(ACCOUNT_FEATURE.SPACES);

  const [sortBy, setSortBy] = useState(CHANNEL_SORT_BY.NAME);
  const [sortDirection, setSortDirection] = useState(CHANNEL_SORT_DIRECTION.ASC);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  const [channelIDToEdit, setChannelIDToEdit] = useState('');
  const [channelNameToEdit, setChannelNameToEdit] = useState('');

  const sortedChannels = useMemo(
    () => accountChannels.sort(dynamicallySort(sortBy, sortDirection)),
    [accountChannels, sortBy, sortDirection]
  );

  const handleChannelsSort = useCallback(
    (sortByProperty) => {
      const sortingDirection =
        sortBy === sortByProperty && sortDirection === CHANNEL_SORT_DIRECTION.ASC
          ? CHANNEL_SORT_DIRECTION.DESC
          : CHANNEL_SORT_DIRECTION.ASC;

      setSortDirection(sortingDirection);
      setSortBy(sortByProperty);
    },
    [sortBy, sortDirection]
  );

  const closeModal = useCallback(() => {
    setChannelIDToEdit('');
    setChannelNameToEdit('');
    setIsEditModalOpen(false);
    setIsCreateModalOpen(false);
  }, []);

  const openCreateModal = useCallback(() => {
    closeModal();
    setIsCreateModalOpen(true);
  }, [closeModal]);

  const openEditModal = useCallback(
    ({ id, name, token }) => {
      if (isSpacesEnabled) {
        history.push(`/space/${token}`);
        return;
      }
      closeModal();
      setIsEditModalOpen(true);
      setChannelIDToEdit(id);
      setChannelNameToEdit(name);
    },
    [closeModal, isSpacesEnabled, history]
  );

  const handleChannelCreate = useCallback(
    async (name) => {
      const { error } = await dispatch(createChannel({ name, trigger: 'Channels@handleChannelCreate' }));

      if (error?.status === 401) {
        // eslint-disable-next-line no-console
        console.error('Received 401 response from handleChannelCreate. Redirecting to login.');
        reAuth(location.pathname, encodeURIComponent(location.search));
        return;
      }

      closeModal();
    },
    [dispatch, location, reAuth, closeModal]
  );

  const handleChannelSave = useCallback(
    async (name) => {
      const { error } = await dispatch(
        updateChannel({ name, id: channelIDToEdit, trigger: 'Channels@handleChannelSave' })
      );

      if (error?.status === 401) {
        // eslint-disable-next-line no-console
        console.error('Received 401 response from handleChannelSave. Redirecting to login.');
        reAuth(location.pathname, encodeURIComponent(location.search));
        return;
      }

      closeModal();
    },
    [dispatch, channelIDToEdit, location, reAuth, closeModal]
  );

  useEffect(() => {
    const authorized = checkAuthorization('channels');
    if (!authorized) {
      return;
    }

    if (isUserRolesEnabled && !canManageChannels) {
      history.push('/dashboard');
    }
  }, [canManageChannels, isUserRolesEnabled, checkAuthorization, accountId, history]);

  return (
    <div className="channels-content content-container">
      <AccountNav />
      <div className="channels-header header-container">
        <h2 className="channels-header-title">{accountName} Channels</h2>
        {(isUserRolesEnabled || userRole === USER_ROLE.ADMIN) && (
          <div className="channels-header-buttons">
            <button
              type="button"
              id="channelsCreateBtn"
              className="btn btn-channels-create"
              onClick={openCreateModal}
              data-modal="create"
            >
              Add channel
            </button>
          </div>
        )}
      </div>

      <div className="channels-table">
        <CustomTable
          handleSort={handleChannelsSort}
          headerItems={CHANNEL_TABLE_HEADER_ITEMS}
          selectedHeaderItem={sortBy}
          sortDirection={sortDirection}
          isLoading={!didChannelsLoad}
          tableBody={sortedChannels.map((item) => (
            <ChannelTableRowRenderer
              key={item.token}
              handleItemClicked={openEditModal}
              enableAdminOverride={userRole === 'ADMIN'}
              item={item}
            />
          ))}
        />
      </div>

      {isEditModalOpen && (
        <ChannelModal
          initialName={channelNameToEdit}
          type={ChannelModalTypes.EDIT}
          isOpen={isEditModalOpen}
          handleOnClose={closeModal}
          handleOnSubmit={handleChannelSave}
        />
      )}
      {isCreateModalOpen && (
        <ChannelModal
          type={ChannelModalTypes.CREATE}
          isOpen={isCreateModalOpen}
          handleOnClose={closeModal}
          handleOnSubmit={handleChannelCreate}
        />
      )}
    </div>
  );
};

Channels.propTypes = {
  checkAuthorization: PropTypes.func.isRequired,
  reAuth: PropTypes.func.isRequired,
};

export default Channels;
